import {
    type ComponentProps,
    type CSSProperties,
    type ReactNode,
    useState
} from "react";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    type NCoreUIKitIcon,
    Breadcrumb,
    Button,
    Text
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    BulletList,
    InlineCode,
    Paragraph,
    CodeBlock,
    Article,
    Preview,
    Section
} from "../../components/content";
import {
    HomeIcon,
    StarIcon
} from "../../anatomy/icons";

const NAME = "Breadcrumb";

const USAGE_CODE = `<Breadcrumb
    maxItems={4}
    size="medium"
>
    <Breadcrumb.List>
        <Breadcrumb.Item>
            <Breadcrumb.Link
                icon={HomeIcon}
                isIconOnly={true}
                title="Home"
                href="/"
            />
        </Breadcrumb.Item>
        <Breadcrumb.Item>
            <Breadcrumb.Link
                href="/reports"
                title="Reports"
            />
        </Breadcrumb.Item>
        <Breadcrumb.Item>
            <Breadcrumb.Page
                isLoading={isFetching}
                title={report.title}
            />
        </Breadcrumb.Item>
    </Breadcrumb.List>
</Breadcrumb>`;

const COMPACT_CODE = `<Breadcrumb
    compactBehaviour={isHeaderBackVisible ? "hidden" : "back"}
>
    ...
</Breadcrumb>`;

const PATH_KEYS = [
    "home",
    "reports",
    "sales",
    "europe",
    "q3"
] as const;

const SIZES = [
    "small",
    "medium",
    "large"
] as const;

const SEPARATORS = [
    "chevron",
    "slash"
] as const;

const INITIAL_DEPTH = 3;

type PathKey = typeof PATH_KEYS[number];

type PathOptions = Omit<ComponentProps<typeof Breadcrumb>, "children"> & {
    onSelect?: (depth: number) => void;
    separatorIcon?: NCoreUIKitIcon;
    disabledKey?: PathKey;
    isLoading?: boolean;
    depth?: number;
};

const CELL_STYLE: CSSProperties = {
    flexDirection: "column",
    display: "flex",
    width: "100%",
    gap: 8
};

const NARROW_STYLE: CSSProperties = {
    flexDirection: "column",
    display: "flex",
    width: 220
};

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("breadcrumbPage.separatorsTitle"),
        id: "separators"
    },
    {
        title: t("section.sizes"),
        id: "sizes"
    },
    {
        title: t("breadcrumbPage.collapseTitle"),
        id: "collapse"
    },
    {
        title: t("section.states"),
        id: "states"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const BreadcrumbPage = () => {
    const {
        t
    } = useDocsI18n();

    const [
        depth,
        setDepth
    ] = useState(INITIAL_DEPTH);

    const pathTitles: Record<PathKey, string> = {
        reports: t("breadcrumbPage.reports"),
        europe: t("breadcrumbPage.europe"),
        sales: t("breadcrumbPage.sales"),
        q3: t("breadcrumbPage.q3"),
        home: t("sample.home")
    };

    const currentTitle = pathTitles[PATH_KEYS[depth - 1] ?? "home"];

    const handleDeeper = () => setDepth(Math.min(depth + 1, PATH_KEYS.length));

    const renderCell = (label: string, node: ReactNode) => <div
        style={CELL_STYLE}
        key={label}
    >
        <Text
            variant="labelSmallSize"
            color="mid"
        >
            {label}
        </Text>
        {node}
    </div>;

    const renderPath = ({
        depth: currentDepth = PATH_KEYS.length,
        separatorIcon,
        disabledKey,
        isLoading,
        onSelect,
        ...props
    }: PathOptions) => <Breadcrumb
        {...props}
    >
        <Breadcrumb.List>
            {
                PATH_KEYS.slice(0, currentDepth).map((key, index) => <Breadcrumb.Item
                    key={key}
                >
                    {
                        separatorIcon ?
                            <Breadcrumb.Separator
                                icon={separatorIcon}
                            />
                        :
                            null
                    }
                    {
                        index === currentDepth - 1 ?
                            <Breadcrumb.Page
                                title={pathTitles[key]}
                                isLoading={isLoading}
                            />
                        :
                            <Breadcrumb.Link
                                icon={key === "home" ? HomeIcon : undefined}
                                onPress={() => onSelect?.(index + 1)}
                                isDisabled={key === disabledKey}
                                isIconOnly={key === "home"}
                                title={pathTitles[key]}
                            />
                    }
                </Breadcrumb.Item>)
            }
        </Breadcrumb.List>
    </Breadcrumb>;

    return <Article>
        <ComponentHeader
            lead={t("breadcrumbPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("breadcrumbPage.overviewText")}
            </Paragraph>
            <Preview
                caption={t("breadcrumbPage.overviewCaption", [
                    currentTitle
                ])}
                isStacked={true}
            >
                {
                    renderPath({
                        onSelect: setDepth,
                        depth
                    })
                }
                <Button
                    isDisabled={depth >= PATH_KEYS.length}
                    title={t("breadcrumbPage.goDeeper")}
                    onPress={handleDeeper}
                    spreadBehaviour="free"
                    variant="outline"
                    type="neutral"
                    size="small"
                />
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("breadcrumbPage.separatorsTitle")}
            id="separators"
        >
            <Paragraph>
                {t("breadcrumbPage.separatorsText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                {
                    SEPARATORS.map((separator) => renderCell(`separator="${separator}"`, renderPath({
                        depth: 3,
                        separator
                    })))
                }
                {
                    renderCell("Breadcrumb.Separator icon", renderPath({
                        separatorIcon: StarIcon,
                        depth: 3
                    }))
                }
            </Preview>
        </Section>
        <Section
            title={t("section.sizes")}
            id="sizes"
        >
            <Paragraph>
                {t("breadcrumbPage.sizesText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                {
                    SIZES.map((size) => renderCell(`size="${size}"`, renderPath({
                        depth: 3,
                        size
                    })))
                }
            </Preview>
        </Section>
        <Section
            title={t("breadcrumbPage.collapseTitle")}
            id="collapse"
        >
            <Paragraph>
                {t("breadcrumbPage.collapseText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                {
                    renderCell("collapseBehaviour=\"never\"", renderPath({
                        collapseBehaviour: "never"
                    }))
                }
                {
                    renderCell("collapseBehaviour=\"always\"", renderPath({
                        collapseBehaviour: "always"
                    }))
                }
                {
                    renderCell(t("breadcrumbPage.narrowLabel"), <div
                        style={NARROW_STYLE}
                    >
                        {
                            renderPath({
                                collapseBehaviour: "auto",
                                maxItems: 10
                            })
                        }
                    </div>)
                }
            </Preview>
            <Paragraph
                isMuted={true}
            >
                {t("breadcrumbPage.compactText")}
            </Paragraph>
            <CodeBlock
                code={COMPACT_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.states")}
            id="states"
        >
            <Paragraph>
                {t("breadcrumbPage.statesText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                {
                    renderCell("Breadcrumb.Link isDisabled={true}", renderPath({
                        disabledKey: "reports",
                        depth: 3
                    }))
                }
                {
                    renderCell("Breadcrumb.Page isLoading={true}", renderPath({
                        isLoading: true,
                        depth: 3
                    }))
                }
            </Preview>
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("breadcrumbPage.a11yLandmark"),
                    t("breadcrumbPage.a11yPage"),
                    t("breadcrumbPage.a11yLink"),
                    t("breadcrumbPage.a11yEllipsis"),
                    t("breadcrumbPage.a11yBack"),
                    t("breadcrumbPage.a11yLoading"),
                    t("breadcrumbPage.a11yMeasure"),
                    t("breadcrumbPage.a11yHidden")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        type: "\"small\" | \"medium\" | \"large\"",
                        description: t("breadcrumbPage.propSize"),
                        defaultValue: "\"medium\"",
                        name: "size"
                    },
                    {
                        description: t("breadcrumbPage.propSeparator"),
                        type: "\"chevron\" | \"slash\"",
                        defaultValue: "\"chevron\"",
                        name: "separator"
                    },
                    {
                        description: t("breadcrumbPage.propCollapseBehaviour"),
                        type: "\"auto\" | \"always\" | \"never\"",
                        defaultValue: "\"auto\"",
                        name: "collapseBehaviour"
                    },
                    {
                        description: t("breadcrumbPage.propCompactBehaviour"),
                        type: "\"back\" | \"collapse\" | \"hidden\"",
                        defaultValue: "\"back\"",
                        name: "compactBehaviour"
                    },
                    {
                        description: t("breadcrumbPage.propMaxItems"),
                        defaultValue: "4",
                        name: "maxItems",
                        type: "number"
                    },
                    {
                        description: t("breadcrumbPage.propItemsBeforeCollapse"),
                        name: "itemsBeforeCollapse",
                        defaultValue: "1",
                        type: "number"
                    },
                    {
                        description: t("breadcrumbPage.propItemsAfterCollapse"),
                        name: "itemsAfterCollapse",
                        defaultValue: "2",
                        type: "number"
                    },
                    {
                        description: t("breadcrumbPage.propAccessibilityLabel"),
                        name: "accessibilityLabel",
                        type: "string"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("breadcrumbPage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("breadcrumbPage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("breadcrumbPage.propsNote")}
            </Paragraph>
            <Paragraph>
                <InlineCode>
                    Breadcrumb.List
                </InlineCode>
                {" "}
                {t("breadcrumbPage.partListText")}
            </Paragraph>
            <Paragraph>
                <InlineCode>
                    Breadcrumb.Item
                </InlineCode>
                {" "}
                {t("breadcrumbPage.partItemText")}
            </Paragraph>
            <Paragraph
                isMuted={true}
            >
                {t("breadcrumbPage.listItemNote")}
            </Paragraph>
            <Paragraph>
                <InlineCode>
                    Breadcrumb.Link
                </InlineCode>
                {" "}
                {t("breadcrumbPage.partLinkText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("breadcrumbPage.propLinkTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("breadcrumbPage.propLinkHref"),
                        type: "string",
                        name: "href"
                    },
                    {
                        description: t("breadcrumbPage.propLinkOnPress"),
                        type: "() => void",
                        name: "onPress"
                    },
                    {
                        description: t("breadcrumbPage.propLinkIcon"),
                        type: "NCoreUIKitIcon",
                        name: "icon"
                    },
                    {
                        description: t("breadcrumbPage.propLinkIsIconOnly"),
                        defaultValue: "false",
                        name: "isIconOnly",
                        type: "boolean"
                    },
                    {
                        description: t("breadcrumbPage.propLinkIsDisabled"),
                        defaultValue: "false",
                        name: "isDisabled",
                        type: "boolean"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("breadcrumbPage.linkNote")}
            </Paragraph>
            <Paragraph>
                <InlineCode>
                    Breadcrumb.Page
                </InlineCode>
                {" "}
                {t("breadcrumbPage.partPageText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("breadcrumbPage.propPageTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("breadcrumbPage.propPageIcon"),
                        type: "NCoreUIKitIcon",
                        name: "icon"
                    },
                    {
                        description: t("breadcrumbPage.propPageIsLoading"),
                        defaultValue: "false",
                        name: "isLoading",
                        type: "boolean"
                    }
                ]}
            />
            <Paragraph>
                <InlineCode>
                    Breadcrumb.Separator
                </InlineCode>
                {" "}
                {t("breadcrumbPage.partSeparatorText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("breadcrumbPage.propSeparatorIcon"),
                        type: "NCoreUIKitIcon",
                        name: "icon"
                    }
                ]}
            />
            <Paragraph>
                <InlineCode>
                    Breadcrumb.Ellipsis
                </InlineCode>
                {" "}
                {t("breadcrumbPage.partEllipsisText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        type: "Array<{ key, title, href?, onPress?, isDisabled? }>",
                        description: t("breadcrumbPage.propEllipsisItems"),
                        name: "items"
                    },
                    {
                        description: t("breadcrumbPage.propEllipsisTitle"),
                        type: "string",
                        name: "title"
                    }
                ]}
            />
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default BreadcrumbPage;
